import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, RefreshCw, MessageSquare, MessageCircle, Pin } from "lucide-react";
import { Link } from "react-router-dom";

/** Admin console: manage forum_categories + sub_forums + live activity feed. */
export default function ForumConsole({ initialTab = "struktur", hideStrukturTab = false } = {}) {
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [catForm, setCatForm] = useState(null);   // { category_id?, label, slug, description, order, active }
  const [subForm, setSubForm] = useState(null);   // { sub_forum_id?, category_id, label, slug, description, order, active }
  const [activity, setActivity] = useState([]);
  const [tab, setTab] = useState(initialTab);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/forum/categories").then((r) => r.data),
        api.get("/admin/forum/sub-forums").then((r) => r.data),
      ]);
      setCats(c);
      setSubs(s);
    } catch (e) { toast.error("Gagal memuat data forum"); }
  };
  const loadActivity = () => api.get("/admin/forum/activity", { params: { limit: 60 } }).then((r) => setActivity(r.data)).catch(() => {});

  useEffect(() => { load(); loadActivity(); }, []);
  useEffect(() => { if (tab === "aktivitas") loadActivity(); }, [tab]);

  const saveCat = async () => {
    if (!catForm?.label) return toast.error("Label wajib");
    try {
      if (catForm.category_id) {
        await api.patch(`/admin/forum/categories/${catForm.category_id}`, catForm);
        toast.success("Kategori diperbarui");
      } else {
        await api.post("/admin/forum/categories", catForm);
        toast.success("Kategori dibuat");
      }
      setCatForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const delCat = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.label}"? Sub-forum & thread akan dilepaskan.`)) return;
    try {
      await api.delete(`/admin/forum/categories/${c.category_id}`);
      toast.success("Kategori dihapus");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const saveSub = async () => {
    if (!subForm?.label || !subForm?.category_id) return toast.error("Label & kategori wajib");
    try {
      if (subForm.sub_forum_id) {
        await api.patch(`/admin/forum/sub-forums/${subForm.sub_forum_id}`, subForm);
        toast.success("Sub-forum diperbarui");
      } else {
        await api.post("/admin/forum/sub-forums", subForm);
        toast.success("Sub-forum dibuat");
      }
      setSubForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const delSub = async (s) => {
    if (!window.confirm(`Hapus sub-forum "${s.label}"?`)) return;
    try {
      await api.delete(`/admin/forum/sub-forums/${s.sub_forum_id}`);
      toast.success("Sub-forum dihapus");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const subsByCat = subs.reduce((m, s) => ({ ...m, [s.category_id]: [...(m[s.category_id] || []), s] }), {});

  // Activity feed helpers — route by entity so we never send a native comment
  // to a legacy endpoint (would 404) or attempt to pin a native discussion.
  const deleteFromFeed = async (item) => {
    if (!item.can_delete) return;
    if (!window.confirm(item.type === "thread" ? "Hapus thread ini permanen?" : "Hapus balasan ini?")) return;
    try {
      if (item.entity === "legacy" && item.type === "thread") {
        await api.delete(`/admin/threads/${item.thread_id}`);
      } else if (item.entity === "native" && item.type === "reply") {
        await api.delete(`/comments/${item.comment_id || item.id}`);
      } else {
        toast.error("Aksi hapus tidak didukung untuk item ini.");
        return;
      }
      toast.success("Item dihapus");
      loadActivity();
    } catch {
      toast.error("Gagal menghapus");
    }
  };
  const pinThreadFromFeed = async (item) => {
    if (!item.can_pin) return;
    try {
      await api.patch(`/admin/threads/${item.thread_id}`, { pinned: !item.pinned });
      toast.success(item.pinned ? "Pin dilepas" : "Thread dipin");
      loadActivity();
    } catch { toast.error("Gagal"); }
  };

  return (
    <div data-testid="forum-console">
      <div className="flex items-center gap-2 border-b border-[#EAE7E0] mb-5">
        {!hideStrukturTab && (
          <button onClick={() => setTab("struktur")} data-testid="forum-tab-struktur"
            className={"px-4 py-2 text-sm border-b-2 -mb-px " + (tab === "struktur" ? "border-[#1E3A8A] text-[#1E3A8A] font-semibold" : "border-transparent text-[#4B5563] hover:text-[#1F2937]")}>
            Struktur
          </button>
        )}
        <button onClick={() => setTab("aktivitas")} data-testid="forum-tab-aktivitas"
          className={"px-4 py-2 text-sm border-b-2 -mb-px " + (tab === "aktivitas" ? "border-[#1E3A8A] text-[#1E3A8A] font-semibold" : "border-transparent text-[#4B5563] hover:text-[#1F2937]")}>
          Aktivitas Live
        </button>
      </div>

      {tab === "struktur" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#4B5563]">Susun Kategori & Sub-Forum tanpa mengubah kode. Threads akan pindah otomatis mengikuti sub_forum_id.</p>
            <Button onClick={() => setCatForm({ label: "", slug: "", description: "", order: 100, active: true })} data-testid="new-category-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white h-9">
              <Plus size={14} className="mr-1" /> Kategori baru
            </Button>
          </div>
          <div className="space-y-2">
            {cats.map((c) => (
              <div key={c.category_id} data-testid={`cat-row-${c.slug}`} className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
                <div className="p-4 flex items-center justify-between gap-3">
                  <button onClick={() => setExpanded({ ...expanded, [c.category_id]: !expanded[c.category_id] })} className="flex items-center gap-2 text-left flex-1 min-w-0">
                    {expanded[c.category_id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <div className="min-w-0">
                      <div className="font-serif-display text-lg text-[#1F2937] truncate">{c.label}</div>
                      <div className="text-xs text-[#4B5563] truncate">/{c.slug} · {subsByCat[c.category_id]?.length || 0} sub-forum</div>
                    </div>
                  </button>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button onClick={() => setSubForm({ category_id: c.category_id, label: "", slug: "", description: "", order: 100, active: true })} data-testid={`add-subforum-${c.slug}`} className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-8 px-3 text-xs">
                      <Plus size={12} className="mr-1" /> Sub-forum
                    </Button>
                    <Button variant="outline" onClick={() => setCatForm({ ...c })} data-testid={`edit-cat-${c.slug}`} className="rounded-full h-8 px-3 border-[#EAE7E0]"><Pencil size={12} /></Button>
                    <Button variant="outline" onClick={() => delCat(c)} data-testid={`del-cat-${c.slug}`} className="rounded-full h-8 px-3 border-[#EAE7E0] text-[#B91C1C]"><Trash2 size={12} /></Button>
                  </div>
                </div>
                {expanded[c.category_id] && (
                  <div className="border-t border-[#EAE7E0] bg-[#FAFAF8] p-3 space-y-1.5">
                    {(subsByCat[c.category_id] || []).length === 0 && <div className="text-xs text-[#4B5563] italic px-2 py-2">Belum ada sub-forum di kategori ini.</div>}
                    {(subsByCat[c.category_id] || []).map((s) => (
                      <div key={s.sub_forum_id} data-testid={`sub-row-${s.slug}`} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-[#EAE7E0]">
                        <div className="flex-1 min-w-0">
                          <Link to={`/forum/sub/${s.slug}`} className="text-sm text-[#1F2937] hover:text-[#1E3A8A]">{s.label}</Link>
                          <div className="text-[10px] text-[#4B5563]">/{s.slug} · order {s.order}</div>
                        </div>
                        {s.description && <div className="text-xs text-[#4B5563] italic max-w-md truncate hidden sm:block">{s.description}</div>}
                        <Button variant="outline" onClick={() => setSubForm({ ...s })} data-testid={`edit-sub-${s.slug}`} className="rounded-full h-7 px-2 border-[#EAE7E0]"><Pencil size={11} /></Button>
                        <Button variant="outline" onClick={() => delSub(s)} data-testid={`del-sub-${s.slug}`} className="rounded-full h-7 px-2 border-[#EAE7E0] text-[#B91C1C]"><Trash2 size={11} /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "aktivitas" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#4B5563]">Feed real-time: thread & balasan terbaru. Aksi cepat: pin / hapus thread.</p>
            <Button onClick={loadActivity} data-testid="refresh-activity" variant="outline" className="rounded-full h-9 px-3 border-[#EAE7E0]"><RefreshCw size={12} className="mr-1" /> Muat ulang</Button>
          </div>
          <div className="space-y-2">
            {activity.length === 0 && <div className="text-center text-[#4B5563] p-8" data-testid="activity-empty">Tidak ada aktivitas terbaru.</div>}
            {activity.map((it, i) => {
              const isThread = it.type === "thread";
              const entityBadge = it.entity === "native"
                ? { label: "RUANG", cls: "bg-[#E8F5EE] text-[#166534]" }
                : { label: "LEGACY", cls: "bg-[#F3F1ED] text-[#4B5563]" };
              const typeBadge = isThread
                ? { label: it.entity === "native" ? "DISKUSI BARU" : "THREAD BARU", cls: "bg-[#EFF6FF] text-[#1E3A8A]" }
                : { label: it.parent_comment_id ? "BALASAN NESTED" : "BALASAN", cls: "bg-[#FEF3C7] text-[#92400E]" };
              return (
                <div key={`${it.entity}-${it.type}-${it.id}-${i}`} data-testid={`activity-item-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4 flex items-start gap-3">
                  <div className={"w-8 h-8 rounded-full flex items-center justify-center shrink-0 " + (isThread ? "bg-[#EFF6FF] text-[#1E3A8A]" : "bg-[#FEF3C7] text-[#92400E]")}>
                    {isThread ? <MessageSquare size={14} /> : <MessageCircle size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-widest text-[#4B5563] flex items-center gap-2 flex-wrap">
                      <span className={"px-2 py-0.5 rounded-full " + typeBadge.cls} data-testid={`activity-type-${i}`}>{typeBadge.label}</span>
                      <span className={"px-2 py-0.5 rounded-full text-[10px] " + entityBadge.cls} data-testid={`activity-entity-${i}`}>{entityBadge.label}</span>
                      <span className="normal-case tracking-normal text-[#1F2937] font-semibold">{it.author_name || "—"}</span>
                      <span>· {it.created_at ? new Date(it.created_at).toLocaleString("id-ID") : "—"}</span>
                      {it.pinned && <span className="inline-flex items-center gap-1 text-[#E07A5F]"><Pin size={10} /> pinned</span>}
                    </div>
                    {isThread && it.link_url && it.title && (
                      <Link to={it.link_url} data-testid={`activity-link-${i}`} className="font-serif-display text-base text-[#1F2937] hover:text-[#1E3A8A]">{it.title}</Link>
                    )}
                    {!isThread && it.context_title && (
                      <div className="text-xs text-[#4B5563] italic mt-0.5 truncate">↳ pada: {it.context_title}</div>
                    )}
                    <p className="text-sm text-[#4B5563] line-clamp-2 mt-0.5">
                      {it.link_url ? <Link to={it.link_url} data-testid={`activity-body-link-${i}`} className="hover:text-[#1E3A8A]">{it.body_preview}</Link> : it.body_preview}
                    </p>
                  </div>
                  {(it.can_pin || it.can_delete) && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {it.can_pin && (
                        <Button variant="outline" onClick={() => pinThreadFromFeed(it)} data-testid={`activity-pin-${i}`} className="rounded-full h-7 px-2 border-[#EAE7E0] text-xs"><Pin size={10} className="mr-1" /> {it.pinned ? "Unpin" : "Pin"}</Button>
                      )}
                      {it.can_delete && (
                        <Button variant="outline" onClick={() => deleteFromFeed(it)} data-testid={`activity-del-${i}`} className="rounded-full h-7 px-2 border-[#EAE7E0] text-[#B91C1C] text-xs"><Trash2 size={10} className="mr-1" /> Hapus</Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Category form */}
      <Dialog open={!!catForm} onOpenChange={(v) => !v && setCatForm(null)}>
        <DialogContent data-testid="cat-form-dialog">
          <DialogHeader><DialogTitle>{catForm?.category_id ? "Edit kategori" : "Kategori baru"}</DialogTitle></DialogHeader>
          {catForm && (
            <div className="space-y-3">
              <Input placeholder="Label (mis: Forum Jenjang)" value={catForm.label} onChange={(e) => setCatForm({ ...catForm, label: e.target.value })} data-testid="cat-form-label" />
              <Input placeholder="Slug (opsional, auto dari label)" value={catForm.slug || ""} onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })} data-testid="cat-form-slug" />
              <Textarea rows={2} placeholder="Deskripsi (opsional)" value={catForm.description || ""} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} data-testid="cat-form-desc" />
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" placeholder="Urutan" value={catForm.order ?? 100} onChange={(e) => setCatForm({ ...catForm, order: Number(e.target.value) })} data-testid="cat-form-order" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setCatForm(null)} className="rounded-full h-10">Batal</Button>
                <Button onClick={saveCat} data-testid="cat-form-save" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10">Simpan</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sub-forum form */}
      <Dialog open={!!subForm} onOpenChange={(v) => !v && setSubForm(null)}>
        <DialogContent data-testid="sub-form-dialog">
          <DialogHeader><DialogTitle>{subForm?.sub_forum_id ? "Edit sub-forum" : "Sub-forum baru"}</DialogTitle></DialogHeader>
          {subForm && (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Parent kategori</div>
                <Select value={subForm.category_id} onValueChange={(v) => setSubForm({ ...subForm, category_id: v })}>
                  <SelectTrigger data-testid="sub-form-cat"><SelectValue placeholder="Pilih kategori parent" /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Label (mis: SD Binus)" value={subForm.label} onChange={(e) => setSubForm({ ...subForm, label: e.target.value })} data-testid="sub-form-label" />
              <Input placeholder="Slug (opsional)" value={subForm.slug || ""} onChange={(e) => setSubForm({ ...subForm, slug: e.target.value })} data-testid="sub-form-slug" />
              <Textarea rows={2} placeholder="Deskripsi (opsional)" value={subForm.description || ""} onChange={(e) => setSubForm({ ...subForm, description: e.target.value })} data-testid="sub-form-desc" />
              <Input type="number" placeholder="Urutan" value={subForm.order ?? 100} onChange={(e) => setSubForm({ ...subForm, order: Number(e.target.value) })} data-testid="sub-form-order" />
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setSubForm(null)} className="rounded-full h-10">Batal</Button>
                <Button onClick={saveSub} data-testid="sub-form-save" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10">Simpan</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
